import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import Close from '@mui/icons-material/Close';
import FolderSharp from '@mui/icons-material/FolderSharp';
import ListIcon from '@mui/icons-material/List';
import EntitiesTaskAddDialog from 'components/organisms/Dialogs/EntitiesTaskAddDialog/EntitiesTaskAddDialog';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import StyledEntitiesActionbar
  from 'components/organisms/Snackbars/EntitiesActionbar/EntitiesActionbar.styles';
import CollectionSelectionDialog
  from 'components/organisms/Dialogs/CollectionSelectionDialog/CollectionSelectionDialog';
import Download from '@mui/icons-material/Download';
import BackupTable from '@mui/icons-material/BackupTable';
import FolderZip from '@mui/icons-material/FolderZip';
import Edit from '@mui/icons-material/Edit';
import utils from 'helpers/utils';
import {useSnackbar} from 'components/organisms/Providers/SnackbarProvider/SnackbarProvider';
import constants from 'helpers/constants';
import {useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import {useCollectionGet} from 'services/collection/collection.hooks';
import EntitiesBulkDialog from 'components/organisms/Dialogs/EntitiesBulkDialog/EntitiesBulkDialog';
import EntitiesExportSettingsDialog
  from 'components/organisms/Dialogs/EntitiesExportSettingsDialog/EntitiesExportSettingsDialog';
import Code from '@mui/icons-material/Code';

const EntitiesActionbar = React.forwardRef((props, ref) => {
  const {
    tableProvider,
    listSelection,
    data,
    showDownload,
    ...innerProps
  } = useComponentProps(props, 'EntitiesActionbar');

  const authorize = useAuthorize();
  const snackbar = useSnackbar();
  const dialogControl = useDialogControl();

  const listState = useEffectItem(tableProvider.appliedListState());

  const entities = listSelection.dataSelection;

  const client = tableProvider.context?.data;
  const universeCollection = useCollectionGet({collectionId: client?.universeCollectionId},
    {enabled: Boolean(client?.universeCollectionId)})?.data;

  const clearSelectionEvent = useEffectEvent(listSelection.clearSelection);
  const filterSelectionEvent = useEffectEvent(listSelection.filterSelection);
  const entityAssignEvent = useEffectEvent(tableProvider.updaters?.assignItems);
  const entityDownloadEvent = useEffectEvent(tableProvider.updaters?.downloadItems);
  const entitySelectedEvent = useEffectEvent(tableProvider.updaters?.selectedItems);
  const entityApplyActionsEvent = useEffectEvent(tableProvider.updaters?.applyActions);
  const entityCreateTaskEvent = useEffectEvent(tableProvider.updaters?.createTask);

  const actions = useMemo(() => {
    const actions = [];

    actions.push({
      label: 'Remove selection',
      icon: Close,
      ButtonProps: {
        variant: 'outlined',
        color: 'white'
      },
      onClick: () => {
        clearSelectionEvent?.();
      }
    });

    if (listSelection.count <= listSelection.max) {
      actions.push({
        label: 'Add to collection',
        auth: utils.createAuth({attribute: 'collection.update'}),
        icon: FolderSharp,
        onClick: (e) => {
          let selected, selectedFailed = false;

          const handleClose = (e, reason) => {
            if (reason === 'saveButtonClick') {
              clearSelectionEvent?.();
            }
            dialogControl.hide();
          };

          const handleSubmit = (addedCollections) => {
            const message = `Adding the compan${listSelection.count === 1 ? 'y' : 'ies'} to the collection${addedCollections.length === 1 ? '' : 's'}`;
            return utils.observePromise(utils.wait(() => {
              if (selectedFailed) {
                return Promise.reject();
              } else {
                return utils.isDefined(selected);
              }
            }, constants.delay.selection).then(() => {
              const hasEps = selected.some((entity) => !entity.originalEntityId);
              if (addedCollections.length > 0 && hasEps) {
                return entityAssignEvent?.({
                  collectionId: addedCollections[0].collectionId,
                  entityIds: selected?.map((e) => +e.entityId)
                })
                  .then(() => Promise.all(addedCollections.slice(1).map((collection) => {
                    return entityAssignEvent?.({
                      collectionId: collection.collectionId,
                      entityIds: selected?.map((e) => +e.entityId)
                    });
                  })));
              } else {
                return Promise.all(addedCollections.map((collection) => {
                  return entityAssignEvent?.({
                    collectionId: collection.collectionId,
                    entityIds: selected?.map((e) => +e.entityId)
                  });
                }));
              }
            }), () => {
              return snackbar.show(`${message}...`, null, {
                color: 'info',
                autoHideDuration: null
              });
            }, () => {
              return snackbar.show(`The compan${listSelection.count === 1 ? 'y' : 'ies'} ${listSelection.count === 1 ? 'was' : 'were'} added to the collection${addedCollections.length === 1 ? '' : 's'}`, null,
                {color: 'success', autoHideDuration: constants.delay.success});
            }, () => {
              return snackbar.show(`${message} failed`, null,
                {color: 'error', autoHideDuration: constants.delay.error});
            }, (id) => {
              snackbar.hide(id);
            });
          };

          const showDialog = (collections) => {
            dialogControl.show(<CollectionSelectionDialog placement="top"
                                                          title="Add to collection"
                                                          subtitle={`For ${utils.formatNumber(listSelection.count)} compan${listSelection.count === 1 ? 'y' : 'ies'}`}
                                                          collections={collections}
                                                          hideCollections={true}
                                                          onSubmit={handleSubmit}/>, true, handleClose);
          }

          if (listSelection.count === entities.length) {
            const collections = entities.reduce((a, e) => {
              e.collections.forEach((ce) => {
                if (!a.find((c) => +c.collectionId === +ce.collectionId)) {
                  if (!entities.some((e1) => !e1.collections.find((c) => +c.collectionId === +ce.collectionId))) {
                    a.push(ce);
                  }
                }
              })
              return a;
            }, []);

            selected = entities;
            showDialog(collections);
          } else {
            showDialog();
            entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
              selected = filterSelectionEvent?.(entities);
            }, () => {
              dialogControl.hide();
              selectedFailed = true;
              snackbar.show('Retrieving selection data failed', null,
                {color: 'error', autoHideDuration: constants.delay.error});
            });
          }

          e.preventDefault();
        }
      })

      actions.push({
        label: 'Add task',
        auth: utils.createAuth({attribute: 'entity.task.create'}),
        icon: ListIcon,
        onClick: (e) => {
          let selected, selectedFailed = false;

          const handleClose = (e, reason) => {
            if (reason === 'saveButtonClick') {
              clearSelectionEvent?.();
            }
            dialogControl.hide();
          };

          const handleSubmit = (task) => {
            const message = `Adding the task for the compan${listSelection.count === 1 ? 'y' : 'ies'}`;
            return utils.observePromise(utils.wait(() => {
              if (selectedFailed) {
                return Promise.reject();
              } else {
                return utils.isDefined(selected);
              }
            }, constants.delay.selection).then(() => {
              return entityCreateTaskEvent?.(selected, task);
            }), () => {
              return snackbar.show(`${message}...`, null, {
                color: 'info',
                autoHideDuration: null
              });
            }, () => {
              return snackbar.show(`The task was added to the compan${listSelection.count === 1 ? 'y' : 'ies'}`, null,
                {color: 'success', autoHideDuration: constants.delay.success});
            }, () => {
              return snackbar.show(`${message} failed`, null,
                {color: 'error', autoHideDuration: constants.delay.error});
            }, (id) => {
              snackbar.hide(id);
            });
          }

          const showDialog = () => {
            dialogControl.show(<EntitiesTaskAddDialog placement="top"
                                                      count={listSelection.count}
                                                      onSubmit={handleSubmit}/>, true, handleClose);
          }

          showDialog();
          if (listSelection.count !== entities.length) {
            entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
              selected = filterSelectionEvent?.(entities);
            }, () => {
              dialogControl.hide();
              selectedFailed = true;
              snackbar.show('Retrieving selection data failed', null,
                {color: 'error', autoHideDuration: constants.delay.error});
            });
          } else {
            selected = entities;
          }

          e.preventDefault();
        }
      });

      const bulkActions = constants.data.entityBulkActions
        .filter((eba) => {
          return ![constants.entity.bulkActions.setCollectionTags].includes(eba.value) &&
            (universeCollection?.tagGroups?.length > 0 || ![constants.entity.bulkActions.setClientTags].includes(eba.value));
        })
        .filter((eba) => ![constants.entity.bulkActions.setCollectionTags].includes(eba.value))
        .filter((eba) => eba.edit && authorize({attribute: `collection.entity.bulk.${eba.value}`}));

      actions.push({
        label: `Edit compan${listSelection.count === 1 ? 'y' : 'ies'}`,
        auth: bulkActions.length > 0 ? utils.createAuth({attribute: 'entity.bulk'}) :
          utils.createAuth({attribute: 'system.null'}),
        icon: Edit,
        onClick: (e) => {
          let selected, selectedFailed = false;

          const handleClose = (e, reason) => {
            if (reason === 'saveButtonClick') {
              clearSelectionEvent?.();
            }
            dialogControl.hide();
          };

          const handleSubmit = (action) => {
            const actions = [action];

            const message = `Saving the compan${listSelection.count === 1 ? 'y' : 'ies'}`;
            return utils.observePromise(utils.wait(() => {
              if (selectedFailed) {
                return Promise.reject();
              } else {
                return utils.isDefined(selected);
              }
            }, constants.delay.selection).then(() => {
              return entityApplyActionsEvent?.({entityIds: selected.map((e) => +e.entityId), actions});
            }), () => {
              return snackbar.show(`${message}...`, null, {
                color: 'info',
                autoHideDuration: null
              });
            }, () => {
              return snackbar.show(`The compan${listSelection.count === 1 ? 'y' : 'ies'} were saved`, null,
                {color: 'success', autoHideDuration: constants.delay.success});
            }, () => {
              return snackbar.show(`${message} failed`, null,
                {color: 'error', autoHideDuration: constants.delay.error});
            }, (id) => {
              snackbar.hide(id);
            });
          }

          dialogControl.show(<EntitiesBulkDialog placement="top"
                                                 count={listSelection.count}
                                                 bulkActions={bulkActions}
                                                 universeCollection={universeCollection}
                                                 fieldData={tableProvider.fieldData}
                                                 onSubmit={handleSubmit}/>, true, handleClose);

          if (listSelection.count !== entities.length) {
            entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
              selected = filterSelectionEvent?.(entities);
            }, () => {
              dialogControl.hide();
              selectedFailed = true;
              snackbar.show('Retrieving selection data failed', null,
                {color: 'error', autoHideDuration: constants.delay.error});
            });
          } else {
            selected = entities;
          }

          e.preventDefault();
        }
      });
    }

    if (showDownload) {
      actions.push({
        label: 'Download',
        auth: utils.createAuth({attribute: 'entity.download'}),
        icon: Download,
        ActionButtonProps: {
          DropDownPopperProps: {
            ContextPopperProps: {
              density: 'densest',
              transparent: true,
              outlined: false,
              shadows: false,
              strategy: 'fixed',
              stretch: false,
              offset: [0, 8]
            }
          }
        },
        children: [
          {
            label: 'Download .xlsx',
            auth: utils.createAuth({attribute: 'entity.download.xlsx'}),
            icon: BackupTable,
            onClick: (e) => {
              let selected, selectedFailed = false;

              const handleClose = (e, reason) => {
                if (reason === 'saveButtonClick') {
                  clearSelectionEvent?.();
                }
                dialogControl.hide();
              };

              const handleSubmit = (settings) => {
                const downloadingSnackbar = snackbar.show('Downloading company data...', null, {
                  color: 'info',
                  autoHideDuration: null
                });

                utils.wait(() => {
                  if (selectedFailed) {
                    return Promise.reject();
                  } else {
                    return utils.isDefined(selected);
                  }
                }, constants.delay.selection).then(() => {
                  const filename = `${client.name}.xlsx`;

                  const fields = settings.columnSelection !== 'selectedColumns' ? null :
                    Object.keys(tableProvider.columnState.columnVisibility ?? {})
                      .filter((k) => tableProvider.columnState.columnVisibility[k])
                      .map((k) => k.split('|')[0]);

                  entityDownloadEvent?.({
                    filename: filename,
                    fields: fields,
                    entityIds: selected.map((e) => e.entityId),
                    filters: {
                      ...utils.filter2Object(listState.filter),
                      search: listState.search
                    }
                  })
                    .then((res) => {
                      utils.bytesDownload(res.response.data, filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                    })
                })
                  .catch((error) => {
                    if (error?.response?.status === constants.http.status.conflict) {
                      snackbar.show('The company export limit has been reached', null,
                        {color: 'warning', autoHideDuration: constants.delay.warning});
                    } else {
                      snackbar.show(`Downloading compan${listSelection.count === 1 ? 'y' : 'ies'} failed`, null,
                        {color: 'error', autoHideDuration: constants.delay.error});
                    }
                  })
                  .finally(() => {
                    snackbar.hide(downloadingSnackbar);
                  });
              }

              const showDialog = () => {
                dialogControl.show(<EntitiesExportSettingsDialog placement="top"
                                                                 count={listSelection.count}
                                                                 onSubmit={handleSubmit}/>, true, handleClose);
              }

              showDialog();
              if (listSelection.count !== entities.length) {
                entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
                  selected = filterSelectionEvent?.(entities);
                }, () => {
                  dialogControl.hide();
                  selectedFailed = true;
                  snackbar.show('Retrieving selection data failed', null,
                    {color: 'error', autoHideDuration: constants.delay.error});
                });
              } else {
                selected = entities;
              }

              e.preventDefault();
            }
          },
          {
            label: 'Download .json',
            auth: utils.createAuth({attribute: 'entity.download.json'}),
            icon: Code,
            onClick: (e) => {
              const doDownload = (selected) => {
                const filename = `${client.name}.json`;
                entityDownloadEvent?.({
                  json: true,
                  filename: filename,
                  entityIds: selected.map((e) => e.entityId),
                  filters: {
                    ...utils.filter2Object(listState.filter),
                    search: listState.search
                  }
                })
                  .then(() => {
                    snackbar.show('Json download is scheduled', null,
                      {color: 'success', autoHideDuration: constants.delay.success});
                    clearSelectionEvent?.();
                  })
                  .catch((error) => {
                    if (error?.response?.status === constants.http.status.conflict) {
                      snackbar.show('The company export limit has been reached', null,
                        {color: 'warning', autoHideDuration: constants.delay.warning});
                    } else {
                      snackbar.show(`Download scheduling failed`, null,
                        {color: 'error', autoHideDuration: constants.delay.error});
                    }
                  });
              }

              if (listSelection.count !== entities.length) {
                entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
                  doDownload(filterSelectionEvent?.(entities));
                }, () => {
                  dialogControl.hide();
                  snackbar.show('Retrieving selection data failed', null,
                    {color: 'error', autoHideDuration: constants.delay.error});
                });
              } else {
                doDownload(entities);
              }

              e.preventDefault();
            }
          },
          {
            label: 'Download .zip',
            auth: utils.createAuth({attribute: 'entity.download.zip'}),
            icon: FolderZip,
            onClick: (e) => {
              const doDownload = (selected) => {
                const filename = `${client.name}.zip`;
                entityDownloadEvent?.({
                  zip: true,
                  filename: filename,
                  entityIds: selected.map((e) => e.entityId),
                  filters: {
                    ...utils.filter2Object(listState.filter),
                    search: listState.search
                  }
                })
                  .then(() => {
                    snackbar.show('Zip download is scheduled', null,
                      {color: 'success', autoHideDuration: constants.delay.success});
                    clearSelectionEvent?.();
                  })
                  .catch((error) => {
                    if (error?.response?.status === constants.http.status.conflict) {
                      snackbar.show('The company export limit has been reached', null,
                        {color: 'warning', autoHideDuration: constants.delay.warning});
                    } else {
                      snackbar.show(`Download scheduling failed`, null,
                        {color: 'error', autoHideDuration: constants.delay.error});
                    }
                  });
              }

              if (listSelection.count !== entities.length) {
                entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
                  doDownload(filterSelectionEvent?.(entities));
                }, () => {
                  dialogControl.hide();
                  snackbar.show('Retrieving selection data failed', null,
                    {color: 'error', autoHideDuration: constants.delay.error});
                });
              } else {
                doDownload(entities);
              }

              e.preventDefault();
            }
          }
        ]
      });
    }
    return actions;
  }, [showDownload, listState, listSelection.count, listSelection.dataCount, listSelection.max,
    client, entities, universeCollection, clearSelectionEvent, filterSelectionEvent,
    tableProvider.fieldData, tableProvider.columnState.columnVisibility,
    authorize, snackbar, dialogControl, entityApplyActionsEvent,
    entityDownloadEvent, entityAssignEvent, entitySelectedEvent, entityCreateTaskEvent]);

  const renderTitle = () => {
    const count = utils.formatNumber(listSelection.count ?? 0);
    const title = `${count} compan${listSelection.count === 1 ? 'y' : 'ies'}`;
    if (actions?.length > 1) {
      return title;
    } else {
      const max = utils.formatNumber(listSelection.max ?? 0);
      return `${title} (max ${max})`
    }
  }

  return <StyledEntitiesActionbar ref={ref} {...innerProps}
                                  title={renderTitle()}
                                  actions={actions} />
});

EntitiesActionbar.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  tableProvider: PropTypes.any,
  listSelection: PropTypes.object,
  data: PropTypes.array,
  showDownload: PropTypes.bool
};

export default EntitiesActionbar;
